import React, { useEffect, useState } from 'react'
import { ColumnsType } from 'antd/lib/table'

import { SorterResult, TableRowSelection } from 'antd/lib/table/interface'
import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { IResearchFilter } from '../../model/types/ResearchFilter.interface'
import { selectResearchFilters } from '../../model/researchSlice'
import { useDispatch } from 'react-redux'
import {
  selectCompetitorsFilter,
  selectCompetitorsPagination,
  setCompetitorsPagination,
  setCompetitorsSites,
} from '../../model/competitorsSlice'
import { ICompetitorsFilter } from '../../model/types/CompetitorsFilter.interface'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import { IPagination } from '../../../../shared/types/Pagination.interface'
import { usePreviousValue } from '../../../../shared/lib/hooks/UsePreviousValue'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import { ExportDrop } from '../../../../shared/components/cards/export-drop/ExportDrop'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { useTranslation } from 'react-i18next'
import {
  GetResearchCompetitorsApiArg,
  ResearchCompetitorsItem,
  ResearchTableArg,
  useGetResearchCompetitorsQuery,
  useExportCompetitors,
} from '../../../../app/api'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { CompetitorAdsCountCell } from '../../ui/cells/CompetitorAdsCountCell'
import { TopKeywordCell } from '../../ui/cells/TopKeywordCell'
import { DomainCell } from '../../ui/cells/DomainCell'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import clsx from 'clsx'

import styles from './CompetitorsTable.module.scss'

export const CompetitorsTable = () => {
  const { t } = useTranslation()
  const { handleSendMessage } = useAiChat()
  const dispatch = useDispatch()
  const researchFilters = useAppSelector<IResearchFilter>(selectResearchFilters)
  const competitorsFilter = useAppSelector<ICompetitorsFilter>(selectCompetitorsFilter)
  const pagination = useAppSelector<IPagination>(selectCompetitorsPagination)
  const { current, pageSize, total } = pagination
  const [countRows, setCountRows] = useState<number>(0)
  const [queryParams, setQueryParams] = useState<
    Partial<ResearchTableArg<GetResearchCompetitorsApiArg>>
  >({
    page: current,
    'per-page': pageSize,
  })
  const {
    sites,
    dateRange: { date },
  } = competitorsFilter
  const { project, country, domain } = researchFilters
  const { data, isLoading, isFetching } = useGetResearchCompetitorsQuery(
    queryParams as GetResearchCompetitorsApiArg,
    {
      skip: !(queryParams?.projectIds as number[] | undefined)?.length,
    },
  )
  const previousProject = usePreviousValue(project)
  const [exportFile, { isFetching: exportCompetitorsLoading }] = useExportCompetitors()

  useEffect(() => {
    if (!data?.data) return
    setCountRows(data.data.total)

    if (!sites) {
      dispatch(setCompetitorsSites((data.data.items ?? []).slice(0, 5).map((item) => item.id)))
    }
  }, [data])

  useEffect(() => {
    const project_ids = project ? [project] : []

    setQueryParams({
      ...queryParams,
      projectIds: project_ids as unknown as number[],
      'country_ids[]': country,
      domain,
      date,
    })
  }, [researchFilters, competitorsFilter])

  useEffect(() => {
    if (previousProject && project && previousProject !== project) {
      dispatch(setCompetitorsSites(undefined))
      dispatch(
        setCompetitorsPagination({
          ...pagination,
          current: 1,
        }),
      )
    }
  }, [project])

  const columns: ColumnsType<ResearchCompetitorsItem> = [
    {
      title: t('table.columns.domain'),
      dataIndex: 'domain',
      key: 'domain',
      render: (_: string, record) => <DomainCell {...record} />,
    },
    {
      title: () => (
        <span className={clsx(styles.titleWrap)}>
          {t('table.columns.ads')} <BpTooltip title={t('pages.advertisingResearch.adsTooltip')} />
        </span>
      ),
      dataIndex: 'ads_count',
      key: 'ads_count',
      render: (_: string, record) => <CompetitorAdsCountCell {...record} />,
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('table.columns.topKeyword'),
      dataIndex: 'top_keyword',
      key: 'top_keyword',
      render: (_: string, record) => <TopKeywordCell {...record} />,
    },
    {
      title: t('table.columns.firstFound'),
      key: 'first_link_at',
      dataIndex: 'first_link_at',
    },
    {
      title: t('table.columns.lastUpdate'),
      dataIndex: 'last_update',
      key: 'last_update',
      width: 150,
    },
  ]

  const rowSelection: TableRowSelection<ResearchCompetitorsItem> = {
    selectedRowKeys: sites,
    onChange: (selectedRowKeys) => {
      dispatch(setCompetitorsSites(selectedRowKeys as string[]))
    },
  }

  const handleTableChange = (
    { pageSize, current, total }: IBpPaginationProps,
    sorter?: SorterResult<ResearchCompetitorsItem> | SorterResult<ResearchCompetitorsItem>[],
  ) => {
    dispatch(
      setCompetitorsPagination({
        pageSize: pageSize as number,
        current: current as number,
        total: total as number,
      }),
    )
    setQueryParams({
      ...queryParams,
      page: current,
      'per-page': pageSize,
      sort: useSorter(sorter),
    })
  }

  return (
    <section className={clsx(styles.wrapper)}>
      <div className={clsx(styles.header)}>
        <h3 className={clsx(styles.title)}>
          {t('pages.advertisingResearch.allAdvertisers')}: {countRows}
        </h3>
        <div className={clsx(styles.panel)}>
          {
            {
              bluepear: (
                <AiButton
                  unstyled
                  onClick={() =>
                    handleSendMessage({
                      message:
                        'Please provide a clear guide on how to use the Advertising Research Dashboard and explain its value.',
                    })
                  }
                >
                  How the report works
                </AiButton>
              ),
              robuscan: null,
              default: null,
            }[process.env.REACT_APP_PROJECT ?? 'default']
          }
          <ExportDrop
            columns={[
              { label: t('table.columns.domain'), keys: ['domain'] },
              { label: t('table.columns.ads'), keys: ['ads_count'] },
              { label: t('table.columns.topKeyword'), keys: ['top_keyword'] },
              { label: t('table.columns.firstFound'), keys: ['first_link_at'] },
              { label: t('table.columns.lastUpdate'), keys: ['last_update'] },
            ]}
            loading={exportCompetitorsLoading}
            onExport={(exportParams) => exportFile({ ...queryParams, ...exportParams })}
          />
        </div>
      </div>
      <BpTable
        pagination={{
          current,
          pageSize,
          total,
        }}
        columns={columns}
        data={data as TTableResponse<ResearchCompetitorsItem>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowKey={(record) => record.id}
        rowSelection={rowSelection}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      />
    </section>
  )
}
